import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bot } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useTranslation } from '../../i18n/I18nContext';
import { api } from '../../services/api';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { t, language, setLanguage } = useTranslation();

  const handleLanguageChange = async (nextLanguage: 'de' | 'en') => {
    setLanguage(nextLanguage);
    try {
      await api.post('/settings/batch', { language: nextLanguage });
    } catch {
      // Keep the immediate UI language change even if persisting fails.
    }
  };

  const navigation = [
    { name: t('nav.dashboard'), href: '/', icon: '📊' },
    { name: t('nav.freqtradeBots'), href: '/freqtrade-bots', icon: '🖥️' },
    { name: t('nav.historicBots'), href: '/historic', icon: '📈' },
    { name: t('nav.financialData'), href: '/financedata', icon: '💰' },
    { name: t('nav.strategyLab'), href: '/strategy-lab', icon: '🔬' },
    { name: t('nav.pairlistOptimizer'), href: '/pairlist-selector', icon: '🎯' },
    { name: t('nav.backtestResults'), href: '/backtest', icon: '📉' },
    { name: t('nav.compare'), href: '/compare', icon: '⚖️' },
    { name: t('nav.discovery'), href: '/discovery', icon: '🔍' },
    { name: t('nav.alerts'), href: '/alerts', icon: '🔔' },
    { name: t('nav.settings'), href: '/settings', icon: '⚙️' },
  ];

  const adminNavigation = [{ name: t('nav.users'), href: '/users', icon: '👥' }];

  const allNavigation =
    user?.role === 'admin' ? [...navigation, ...adminNavigation] : navigation;

  const brandIcon = (
    <div className="brand-chip flex h-10 w-10 items-center justify-center">
      <Bot className="h-6 w-6" />
    </div>
  );

  const mobileBrandIcon = (
    <div className="brand-chip flex h-8 w-8 items-center justify-center">
      <Bot className="h-5 w-5" />
    </div>
  );

  return (
    <div className="app-shell min-h-screen">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`sidebar-shell fixed inset-y-0 left-0 z-50 w-64 border-r transform transition-transform duration-300 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b" style={{ borderColor: 'var(--surface-border)' }}>
          <div className="flex items-center gap-2">
            {brandIcon}
            <span className="brand-title text-lg font-bold">
              FreqAlphaDeck
            </span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-[color:var(--surface-text-muted)] hover:text-[color:var(--surface-text)]"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <nav className="px-2 py-4 space-y-1">
          {allNavigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`nav-item flex items-center gap-3 px-4 py-3 ${
                location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                  ? 'nav-item-active'
                  : ''
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="font-medium">{item.name}</span>
            </Link>
          ))}

          <div className="mt-4 px-4">
            <label className="mb-1 block text-xs text-[color:var(--surface-text-muted)]">{t('common.language')}</label>
            <select
              value={language}
              onChange={(e) => void handleLanguageChange(e.target.value as 'de' | 'en')}
              className="shell-input w-full px-3 py-2 text-sm"
            >
              <option value="de">{t('language.german')}</option>
              <option value="en">{t('language.english')}</option>
            </select>
          </div>
          
          {/* Logout in mobile sidebar */}
          <button
            onClick={() => { logout(); setSidebarOpen(false); }}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-900/20 w-full mt-4 border-t pt-4"
            style={{ borderColor: 'var(--surface-border)' }}
          >
            <span className="text-lg">🚪</span>
            <span className="font-medium">{t('common.logout')}</span>
          </button>
        </nav>
      </div>

      {/* Desktop sidebar */}
      <aside className="sidebar-shell hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 lg:border-r">
        <div className="flex items-center gap-2 h-16 px-4 border-b" style={{ borderColor: 'var(--surface-border)' }}>
          {brandIcon}
          <span className="brand-title text-lg font-bold">
            FreqAlphaDeck
          </span>
        </div>
        
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {allNavigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`nav-item flex items-center gap-3 px-4 py-3 ${
                location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                  ? 'nav-item-active'
                  : ''
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="font-medium">{item.name}</span>
            </Link>
          ))}
        </nav>
        
        {/* Logout in desktop sidebar */}
        <div className="px-2 py-4 border-t" style={{ borderColor: 'var(--surface-border)' }}>
          <div className="mb-3 px-4">
            <label className="mb-1 block text-xs text-[color:var(--surface-text-muted)]">{t('common.language')}</label>
            <select
              value={language}
              onChange={(e) => void handleLanguageChange(e.target.value as 'de' | 'en')}
              className="shell-input w-full px-3 py-2 text-sm"
            >
              <option value="de">{t('language.german')}</option>
              <option value="en">{t('language.english')}</option>
            </select>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-900/20 w-full"
          >
            <span className="text-lg">🚪</span>
            <span className="font-medium">{t('common.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64 flex flex-col min-h-screen">
        {/* Mobile top bar */}
        <header className="topbar-shell sticky top-0 z-30 flex items-center justify-between border-b px-4 py-3 backdrop-blur lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="shell-button inline-flex items-center gap-2 px-3 py-2 text-sm font-medium"
            aria-label={t('layout.openMenu')}
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
            <span>{t('common.menu')}</span>
          </button>

          <div className="flex items-center gap-2">
            {mobileBrandIcon}
            <span className="brand-title text-sm font-semibold">
              FreqAlphaDeck
            </span>
          </div>

          <button
            onClick={logout}
            className="shell-button px-3 py-2 text-sm text-red-300 hover:bg-red-900/20"
          >
            {t('common.logout')}
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
